import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { FileDown, FileSpreadsheet, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tl = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;
const PIE = ['#059669', '#0891b2', '#6366f1', '#d97706', '#dc2626', '#7c3aed', '#0d9488', '#ca8a04'];

export const downloadReport = async (url, params, filename) => {
  try {
    const res = await axios.get(url, { params, responseType: 'blob' });
    const href = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(href);
    toast.success('Rapor indirildi');
  } catch (e) {
    toast.error('Rapor oluşturulamadı');
  }
};

const firstOfYear = () => `${new Date().getFullYear()}-01-01`;

export const OrgReports = ({ salons = [], types = [] }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ start: firstOfYear(), end: new Date().toISOString().slice(0, 10), group: 'month', salon_id: 'all', org_type: 'all', status: 'all' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/organizations/reports/overview`, { params: f });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rapor yüklenemedi');
    } finally { setLoading(false); }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  const kpis = s ? [
    ['Organizasyon', s.organization_count, 'text-indigo-700', true],
    ['Oturum', s.session_count, 'text-slate-700', true],
    ['Gerçekleşen Gelir', s.income_total, 'text-emerald-700'],
    ['Tahsil Edilen', s.income_collected, 'text-emerald-600'],
    ['Gerçekleşen Gider', s.expense_total, 'text-rose-700'],
    ['Ödenen Gider', s.expense_paid, 'text-rose-600'],
    ['Net Kâr/Zarar', s.net_profit, s.net_profit < 0 ? 'text-rose-700' : 'text-emerald-800'],
    ['Nakit Sonucu', s.cash_result, 'text-cyan-700'],
    ['Bekleyen Alacak', s.pending_receivable, 'text-amber-700'],
    ['Bekleyen Ödeme', s.pending_payable, 'text-orange-700'],
  ] : [];

  return (
    <div className="space-y-3" data-testid="org-reports-view">
      <Card>
        <CardContent className="p-3 flex flex-wrap items-end gap-2">
          <div><Label className="text-[11px]">Başlangıç</Label><Input type="date" className="h-9 w-36" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="org-report-start" /></div>
          <div><Label className="text-[11px]">Bitiş</Label><Input type="date" className="h-9 w-36" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="org-report-end" /></div>
          <div>
            <Label className="text-[11px]">Kırılım</Label>
            <Select value={f.group} onValueChange={(v) => setF({ ...f, group: v })}>
              <SelectTrigger className="h-9 w-32" data-testid="org-report-group"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Günlük</SelectItem>
                <SelectItem value="week">Haftalık</SelectItem>
                <SelectItem value="month">Aylık</SelectItem>
                <SelectItem value="year">Yıllık</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Salon</Label>
            <Select value={f.salon_id} onValueChange={(v) => setF({ ...f, salon_id: v })}>
              <SelectTrigger className="h-9 w-40" data-testid="org-report-salon"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {salons.map(x => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Tür</Label>
            <Select value={f.org_type} onValueChange={(v) => setF({ ...f, org_type: v })}>
              <SelectTrigger className="h-9 w-40" data-testid="org-report-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Durum</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="h-9 w-36" data-testid="org-report-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="planlandi">Planlandı</SelectItem>
                <SelectItem value="aktif">Aktif</SelectItem>
                <SelectItem value="tamamlandi">Tamamlandı</SelectItem>
                <SelectItem value="iptal">İptal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="h-9" onClick={load} data-testid="org-report-refresh"><RefreshCw className="w-4 h-4" /></Button>
          <div className="flex gap-1 ml-auto">
            <Button className="h-9 bg-slate-800 hover:bg-slate-900" onClick={() => downloadReport(`${API}/organizations/reports/export`, { ...f, format: 'pdf' }, 'organizasyon_raporu.pdf')} data-testid="org-report-pdf">
              <FileDown className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Button className="h-9 bg-emerald-700 hover:bg-emerald-800" onClick={() => downloadReport(`${API}/organizations/reports/export`, { ...f, format: 'excel' }, 'organizasyon_raporu.xlsx')} data-testid="org-report-excel">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading || !data ? (
        <p className="text-center py-10 text-slate-400">Yükleniyor...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5" data-testid="org-report-kpis">
            {kpis.map(([label, val, cls, raw]) => (
              <Card key={label}><CardContent className="p-3">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide truncate">{label}</p>
                <p className={`text-lg font-bold ${cls}`}>{raw ? val : tl(val)}</p>
              </CardContent></Card>
            ))}
          </div>
          <p className="text-xs text-slate-600">Kâr Marjı: <strong>{s.profit_margin}%</strong> · Kırılım: <strong>{data.filters.group_label}</strong></p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card><CardContent className="p-3">
              <p className="text-xs font-semibold mb-2">Dönemsel Gelir / Gider</p>
              {data.periods.length === 0 ? <p className="text-xs text-slate-400 py-24 text-center">Seçili aralıkta veri yok</p> : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.periods}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => tl(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="income" name="Gelir" fill="#059669" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="expense" name="Gider" fill="#dc2626" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>)}
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-xs font-semibold mb-2">Net Kârlılık Trendi</p>
              {data.periods.length === 0 ? <p className="text-xs text-slate-400 py-24 text-center">Seçili aralıkta veri yok</p> : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.periods}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => tl(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="net" name="Net" stroke="#6366f1" strokeWidth={2} />
                  <Line type="monotone" dataKey="collected" name="Tahsilat" stroke="#059669" strokeWidth={2} strokeDasharray="4 2" />
                  <Line type="monotone" dataKey="paid" name="Ödenen" stroke="#dc2626" strokeWidth={2} strokeDasharray="4 2" />
                </LineChart>
              </ResponsiveContainer>)}
            </CardContent></Card>
            {[['Gelir Kategorileri', data.income_by_category], ['Gider Kategorileri', data.expense_by_category]].map(([title, rows]) => (
              <Card key={title}><CardContent className="p-3">
                <p className="text-xs font-semibold mb-2">{title}</p>
                {rows.length === 0 ? <p className="text-xs text-slate-400 py-10 text-center">Kayıt yok</p> : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name?.slice(0, 14)} labelLine={false}>
                        {rows.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => tl(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent></Card>
            ))}
          </div>

          <Card><CardContent className="p-0 overflow-x-auto">
            <p className="text-xs font-semibold p-2.5 border-b">Salon Bazlı Gelir-Gider</p>
            <table className="w-full text-xs" data-testid="org-report-salon-table">
              <thead className="bg-slate-800 text-white">
                <tr><th className="text-left px-3 py-2">Salon</th><th className="text-right px-3 py-2">Oturum</th>
                  <th className="text-right px-3 py-2">Gelir</th><th className="text-right px-3 py-2">Gider</th><th className="text-right px-3 py-2">Net</th></tr>
              </thead>
              <tbody>
                {data.by_salon.length === 0 ? <tr><td colSpan={5} className="text-center py-5 text-slate-400">Kayıt yok</td></tr> :
                  data.by_salon.map((x, i) => (
                    <tr key={x.name + i} className={i % 2 ? 'bg-slate-50' : ''}>
                      <td className="px-3 py-1.5">{x.name}</td>
                      <td className="px-3 py-1.5 text-right">{x.session_count}</td>
                      <td className="px-3 py-1.5 text-right text-emerald-700">{tl(x.income)}</td>
                      <td className="px-3 py-1.5 text-right text-rose-700">{tl(x.expense)}</td>
                      <td className={`px-3 py-1.5 text-right font-bold ${x.net < 0 ? 'text-rose-700' : ''}`}>{tl(x.net)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </CardContent></Card>

          <Card><CardContent className="p-0 overflow-x-auto">
            <p className="text-xs font-semibold p-2.5 border-b">Organizasyon Karşılaştırma (net kârlılığa göre)</p>
            <table className="w-full text-xs" data-testid="org-report-comparison-table">
              <thead className="bg-indigo-800 text-white">
                <tr>
                  <th className="text-left px-3 py-2">Organizasyon</th><th className="text-left px-3 py-2">Tür</th>
                  <th className="text-right px-3 py-2">Oturum</th><th className="text-right px-3 py-2">Gelir</th>
                  <th className="text-right px-3 py-2">Tahsil</th><th className="text-right px-3 py-2">Gider</th>
                  <th className="text-right px-3 py-2">Ödenen</th><th className="text-right px-3 py-2">Net</th>
                  <th className="text-right px-3 py-2">Marj</th>
                </tr>
              </thead>
              <tbody>
                {data.organizations.length === 0 ? <tr><td colSpan={9} className="text-center py-5 text-slate-400">Seçilen dönemde kayıt yok</td></tr> :
                  data.organizations.map((o, i) => (
                    <tr key={o.id} className={i % 2 ? 'bg-slate-50' : ''} data-testid={`org-report-row-${o.id}`}>
                      <td className="px-3 py-1.5"><span className="font-semibold">{o.name}</span><span className="text-[10px] text-slate-500"> · {o.code}</span></td>
                      <td className="px-3 py-1.5">{o.org_type}</td>
                      <td className="px-3 py-1.5 text-right">{o.session_count}</td>
                      <td className="px-3 py-1.5 text-right text-emerald-700">{tl(o.income_total)}</td>
                      <td className="px-3 py-1.5 text-right">{tl(o.income_collected)}</td>
                      <td className="px-3 py-1.5 text-right text-rose-700">{tl(o.expense_total)}</td>
                      <td className="px-3 py-1.5 text-right">{tl(o.expense_paid)}</td>
                      <td className={`px-3 py-1.5 text-right font-bold ${o.net_profit < 0 ? 'text-rose-700' : 'text-emerald-800'}`}>{tl(o.net_profit)}</td>
                      <td className="px-3 py-1.5 text-right">{o.profit_margin}%</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </CardContent></Card>
        </>
      )}
    </div>
  );
};
